import React, { useEffect, useMemo, useState } from 'react';
import AssetSimpleTrendChart from '../../../components/AssetSimpleTrendChart';
import '../../../components/AssetSimpleTrendChart.css';

const PERIOD_KEYS = ['7d', '14d', '1m', '3m', '6m', '1y'];

function fmtNum(v, digits = 2, suffix = '') {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}${suffix}`;
}

function fmtNav(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(6);
}

function fmtAmount(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function alertClass(level) {
  if (level === 'danger') return 'alert-danger';
  if (level === 'warn') return 'alert-warn';
  return '';
}

function findChartIndexByDate(points, date) {
  if (!points?.length) return 0;
  if (!date) return points.length - 1;
  const idx = points.findIndex((p) => p.date === date);
  return idx >= 0 ? idx : points.length - 1;
}

function resolveDefaultChartDate(...pointGroups) {
  for (const points of pointGroups) {
    const last = points?.[points.length - 1]?.date;
    if (last) return last;
  }
  return '';
}

function toTrendPoints(series, valueKey) {
  return (series || [])
    .map((p) => ({
      date: p.date,
      value: p[valueKey]
    }))
    .filter((p) => p.date && p.value != null && Number.isFinite(Number(p.value)));
}

function yieldHighLow(points) {
  if (!points?.length) return null;
  const high = points.reduce((best, p) => (!best || p.value > best.value ? p : best), null);
  const low = points.reduce((best, p) => (!best || p.value < best.value ? p : best), null);
  return { high, low };
}

function PeriodIntervalSummary({ periodPack }) {
  if (!periodPack) return null;
  return (
    <div className="nav-perf-interval-summary">
      <span>
        区间 {periodPack.days_used ?? 0} 个交易日
        {periodPack.calendar_days ? ` · ${periodPack.calendar_days} 自然日` : ''}
      </span>
    </div>
  );
}

function ChartSummaryNav({ periodPack }) {
  if (!periodPack?.markers) return null;
  const markers = periodPack.markers;
  const dd = markers.max_drawdown;
  return (
    <div className="decision-chart-summary">
      {markers.high ? (
        <span className="legend-high">
          净值高点 {markers.high.date} · {fmtNav(markers.high.value)}
        </span>
      ) : null}
      {markers.low ? (
        <span className="legend-low">
          净值低点 {markers.low.date} · {fmtNav(markers.low.value)}
        </span>
      ) : null}
      {markers.drawdown_count != null ? <span>区间回撤 {markers.drawdown_count} 次</span> : null}
      {dd?.bp != null ? (
        <span className="legend-dd">
          最大回撤 {fmtNum(dd.bp, 1, ' bp')}
          {dd.trough_date ? ` · ${dd.trough_date}` : ''}
        </span>
      ) : null}
    </div>
  );
}

function ChartSummaryIncome({ periodPack }) {
  const im = periodPack?.income_markers;
  if (!im) return null;
  return (
    <div className="decision-chart-summary">
      {im.high ? (
        <span className="legend-high">
          万收高点 {im.high.date} · {fmtAmount(im.high.value)}
        </span>
      ) : null}
      {im.low ? (
        <span className="legend-low">
          万收低点 {im.low.date} · {fmtAmount(im.low.value)}
        </span>
      ) : null}
      {im.avg != null ? <span>区间均值 {fmtAmount(im.avg)}（按自然日）</span> : null}
      {im.spread != null ? <span>极差 {fmtAmount(im.spread)}</span> : null}
    </div>
  );
}

function ChartSummaryYield({ points, label }) {
  const hl = yieldHighLow(points);
  if (!hl) return null;
  return (
    <div className="decision-chart-summary">
      {hl.high ? (
        <span className="legend-high">
          {label}高点 {hl.high.date} · {fmtPct(hl.high.value)}
        </span>
      ) : null}
      {hl.low ? (
        <span className="legend-low">
          {label}低点 {hl.low.date} · {fmtPct(hl.low.value)}
        </span>
      ) : null}
    </div>
  );
}

function TrendChartBlock({
  title,
  points,
  activeIndex,
  onActiveIndexChange,
  valueFormatter,
  chartId,
  emptyText,
  summary
}) {
  return (
    <div className="decision-chart-section">
      <h4 className="decision-chart-section-title">{title}</h4>
      <div className="decision-chart-current">
        <span className="decision-chart-current-date">{points[activeIndex]?.date || '—'}</span>
        <span className="decision-chart-current-value">
          {points.length ? valueFormatter(points[activeIndex]?.value) : '—'}
        </span>
      </div>
      {points.length >= 2 ? (
        <AssetSimpleTrendChart
          chartId={chartId}
          points={points}
          activeIndex={activeIndex}
          onActiveIndexChange={onActiveIndexChange}
          showLegend={false}
          preserveAspect="meet"
          height={180}
          valueFormatter={valueFormatter}
        />
      ) : (
        <p className="decision-muted decision-chart-empty">{emptyText || '暂无数据'}</p>
      )}
      {summary}
    </div>
  );
}

function LinkedTrendCharts({
  navPoints,
  incomePoints,
  annual7dPoints,
  annual14dPoints,
  activeDate,
  onActiveDateChange,
  periodPack
}) {
  const navActiveIndex = findChartIndexByDate(navPoints, activeDate);
  const incomeActiveIndex = findChartIndexByDate(incomePoints, activeDate);
  const y7ActiveIndex = findChartIndexByDate(annual7dPoints, activeDate);
  const y14ActiveIndex = findChartIndexByDate(annual14dPoints, activeDate);

  function syncDateFromIndex(points, index) {
    const point = points[index];
    if (point?.date) onActiveDateChange(point.date);
  }

  return (
    <div className="decision-quad-chart-display">
      <TrendChartBlock
        title="单位净值"
        points={navPoints}
        activeIndex={navActiveIndex}
        onActiveIndexChange={(idx) => syncDateFromIndex(navPoints, idx)}
        valueFormatter={(v) => fmtNav(v)}
        chartId="decisionNavTrend"
        summary={<ChartSummaryNav periodPack={periodPack} />}
      />
      <TrendChartBlock
        title="万份收益"
        points={incomePoints}
        activeIndex={incomeActiveIndex}
        onActiveIndexChange={(idx) => syncDateFromIndex(incomePoints, idx)}
        valueFormatter={(v) => fmtAmount(v)}
        chartId="decisionIncomeTrend"
        emptyText="暂无万份收益序列"
        summary={<ChartSummaryIncome periodPack={periodPack} />}
      />
      <TrendChartBlock
        title="7日年化"
        points={annual7dPoints}
        activeIndex={y7ActiveIndex}
        onActiveIndexChange={(idx) => syncDateFromIndex(annual7dPoints, idx)}
        valueFormatter={(v) => fmtPct(v)}
        chartId="decisionAnnual7dTrend"
        emptyText="暂无7日年化序列"
        summary={<ChartSummaryYield points={annual7dPoints} label="7日年化" />}
      />
      <TrendChartBlock
        title="14日年化"
        points={annual14dPoints}
        activeIndex={y14ActiveIndex}
        onActiveIndexChange={(idx) => syncDateFromIndex(annual14dPoints, idx)}
        valueFormatter={(v) => fmtPct(v)}
        chartId="decisionAnnual14dTrend"
        emptyText="暂无14日年化序列"
        summary={<ChartSummaryYield points={annual14dPoints} label="14日年化" />}
      />
    </div>
  );
}

function NavTrendSection({ navPerf, period, onPeriodChange }) {
  const periodPack = navPerf?.periods?.[period];
  const series = periodPack?.series || [];
  const navPoints = useMemo(() => toTrendPoints(series, 'unit_nav'), [series]);
  const incomePoints = useMemo(() => toTrendPoints(series, 'daily_income'), [series]);
  const annual7dPoints = useMemo(() => toTrendPoints(series, 'annual_7d_yield'), [series]);
  const annual14dPoints = useMemo(() => toTrendPoints(series, 'annual_14d_yield'), [series]);
  const [activeDate, setActiveDate] = useState('');

  useEffect(() => {
    setActiveDate((prev) => {
      const fallback = resolveDefaultChartDate(navPoints, incomePoints, annual7dPoints, annual14dPoints);
      if (!fallback) return '';
      const groups = [navPoints, incomePoints, annual7dPoints, annual14dPoints];
      if (groups.some((pts) => prev && findChartIndexByDate(pts, prev) >= 0 && pts.find((p) => p.date === prev))) {
        return prev;
      }
      return fallback;
    });
  }, [navPoints, incomePoints, annual7dPoints, annual14dPoints, period]);

  if (!navPerf?.available) {
    return <p className="decision-muted">暂无净值序列</p>;
  }

  return (
    <div className="nav-trend-chart">
      <div className="nav-perf-toolbar">
        <span className="nav-perf-subtitle">收益走势 · 鼠标/手指划过四图联动</span>
        <PeriodIntervalSummary periodPack={periodPack} />
        <div className="nav-period-tabs">
          {PERIOD_KEYS.map((key) => {
            const p = navPerf.periods?.[key];
            const disabled = !p || p.days_used < 2;
            return (
              <button
                type="button"
                key={key}
                className={`nav-period-tab${period === key ? ' active' : ''}`}
                disabled={disabled}
                onClick={() => onPeriodChange(key)}
              >
                {p?.label || key}
              </button>
            );
          })}
        </div>
      </div>

      {navPoints.length < 2 ? (
        <p className="decision-muted">该周期数据不足，请切换更短区间</p>
      ) : (
        <LinkedTrendCharts
          navPoints={navPoints}
          incomePoints={incomePoints}
          annual7dPoints={annual7dPoints}
          annual14dPoints={annual14dPoints}
          activeDate={activeDate}
          onActiveDateChange={setActiveDate}
          periodPack={periodPack}
        />
      )}
    </div>
  );
}

function ReturnMetricsBoard({ navPerf, period }) {
  if (!navPerf?.available) return null;

  const { returns, periods } = navPerf;
  const periodReturn = periods?.[period]?.period_return_pct;
  const dualMode = returns?.display_mode === 'dual';

  return (
    <div className="nav-metrics-board">
      <div className="nav-metrics-title">
        收益指标
        {dualMode ? <span className="nav-metrics-note">主：区间涨幅 · 副：年化（入库值，且区间自然日充足时显示）</span> : null}
      </div>
      <div className="nav-metrics-grid">
        {(returns?.items || []).map((item) => (
          <div className="nav-metric-item" key={item.key}>
            <div className="nav-metric-label">{item.label}</div>
            {dualMode ? (
              <>
                <div className="nav-metric-value">{fmtNum(item.return_pct, 2, '%')}</div>
                {item.annualized_pct != null ? (
                  <div className="nav-metric-sub">年化 {fmtNum(item.annualized_pct, 2, '%')}</div>
                ) : null}
              </>
            ) : (
              <div className="nav-metric-value">{fmtNum(item.value_pct, 2, '%')}</div>
            )}
          </div>
        ))}
        {returns?.since && (returns.since.annualized_pct != null || returns.since.value_pct != null) ? (
          <div className="nav-metric-item highlight">
            <div className="nav-metric-label">{returns.since.label}</div>
            <div className="nav-metric-value">
              {fmtNum(returns.since.annualized_pct ?? returns.since.value_pct, 2, '%')}
            </div>
            <div className="nav-metric-sub">成立以来年化</div>
          </div>
        ) : null}
        {periodReturn != null ? (
          <div className="nav-metric-item">
            <div className="nav-metric-label">{periods[period]?.label}区间</div>
            <div className="nav-metric-value">{fmtNum(periodReturn, 2, '%')}</div>
          </div>
        ) : null}
      </div>
      {returns?.vs_benchmark?.available ? (
        <div className="nav-benchmark-compare">
          <p className="nav-benchmark-compare-main">
            {returns.vs_benchmark.window_label} vs 业绩比较基准（合成）：
            <strong>{returns.vs_benchmark.vs_text || `${returns.vs_benchmark.vs_bp}bp`}</strong>
            {returns.vs_benchmark.product_cum_text
              ? `（产品 ${returns.vs_benchmark.product_cum_text} / 基准 ${returns.vs_benchmark.synthetic_cum_text}）`
              : ''}
          </p>
          {returns.vs_benchmark.description ? (
            <p className="decision-muted nav-benchmark-compare-desc">{returns.vs_benchmark.description}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RiskMetricsBoard({ navPerf, period }) {
  const risk = navPerf?.periods?.[period]?.risk;
  const recovery = navPerf?.recovery_capability;
  const thresholds = navPerf?.thresholds;
  if (!risk) return null;

  const ddClass = alertClass(risk.alert_level);

  return (
    <div className="nav-metrics-board">
      <div className="nav-metrics-title">风险指标</div>
      <div className="nav-metrics-grid">
        <div className="nav-metric-item">
          <div className="nav-metric-label">回撤次数</div>
          <div className="nav-metric-value">{risk.drawdown_count ?? '—'} 次</div>
        </div>
        <div className={`nav-metric-item ${ddClass}`}>
          <div className="nav-metric-label">最大回撤</div>
          <div className="nav-metric-value">
            {risk.max_drawdown_pct != null
              ? fmtNum(risk.max_drawdown_pct, 2, '%')
              : fmtNum(risk.max_drawdown_bp, 1, ' bp')}
          </div>
          {risk.max_drawdown_trough_date ? (
            <div className="nav-metric-sub">{risk.max_drawdown_trough_date}</div>
          ) : null}
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">修复时长</div>
          <div className="nav-metric-value">
            {risk.max_drawdown_recovery_days != null
              ? `${risk.max_drawdown_recovery_days} 个交易日`
              : '尚未修复'}
          </div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">挂0次数</div>
          <div className="nav-metric-value">{risk.zero_income_days ?? '—'} 次</div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">波动率（日）</div>
          <div className="nav-metric-value">{fmtNum(risk.volatility_bp, 2, ' bp')}</div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">上涨日占比</div>
          <div className="nav-metric-value">{fmtNum(risk.win_rate_pct, 1, '%')}</div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">涨 / 跌天数</div>
          <div className="nav-metric-value">
            {risk.pos_days ?? '—'} / {risk.neg_days ?? '—'}
          </div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">最长连跌</div>
          <div className="nav-metric-value">{risk.max_consecutive_down_days ?? '—'} 天</div>
        </div>
        <div className="nav-metric-item">
          <div className="nav-metric-label">极端单日</div>
          <div className="nav-metric-value">
            {fmtNum(risk.max_single_day_up_bp, 1, ' bp')} / {fmtNum(risk.max_single_day_down_bp, 1, ' bp')}
          </div>
        </div>
      </div>
      {risk.max_drawdown_narrative ? (
        <p className="nav-risk-narrative">{risk.max_drawdown_narrative}</p>
      ) : null}
      {recovery?.available && recovery.text ? (
        <p className="nav-risk-narrative nav-risk-narrative-secondary">{recovery.text}</p>
      ) : null}
      {thresholds ? (
        <p className="decision-muted nav-risk-threshold">
          回撤预警：≥{thresholds.warn_bp}bp 标黄，≥{thresholds.danger_bp}bp 标红（按风险等级调整）
        </p>
      ) : null}
    </div>
  );
}

export default function NavPerformancePanel({ navPerf }) {
  const [period, setPeriod] = useState(navPerf?.default_period || '3m');

  if (!navPerf?.available) {
    return <p className="decision-muted">暂无净值历史，无法展示收益风险分析</p>;
  }

  const activePeriod = navPerf.periods?.[period] ? period : navPerf.default_period || '1m';
  const latest = navPerf.latest;

  return (
    <div className="nav-performance-panel">
      <div className="nav-latest-bar">
        <span>最新 {latest?.date || '—'}</span>
        <span>单位净值 {fmtNav(latest?.unit_nav)}</span>
        {latest?.daily_income != null ? (
          <span>万份收益 {fmtAmount(latest.daily_income)}</span>
        ) : null}
        <span className="decision-muted">共 {navPerf.total_points} 个交易日</span>
      </div>

      <NavTrendSection navPerf={navPerf} period={activePeriod} onPeriodChange={setPeriod} />

      <div className="nav-metrics-split">
        <ReturnMetricsBoard navPerf={navPerf} period={activePeriod} />
        <RiskMetricsBoard navPerf={navPerf} period={activePeriod} />
      </div>
    </div>
  );
}
